/**
 * Step 1 of the RAE booking flow — service selection.
 *
 * Mirrors `rae-booking-step1-service.html`.
 * a11y:
 *   - `<button>` tiles with `aria-pressed` (radio-ish single select).
 *   - Category tabs are real buttons with `aria-selected` via a `role="tab"` list.
 *   - Selection visually combines color + border + check glyph (not color only).
 */

import { useState, type Dispatch } from 'react';
import type { BookingAction } from '../wizardState';
import type { RaeService, ServiceCategory, BookingWizardState } from '../types';
import { RAE_SERVICES } from '../services';
import { Button } from '@/components/primitives';

export interface Step1ServiceProps {
  state: BookingWizardState;
  dispatch: Dispatch<BookingAction>;
  onContinue: () => void;
  onBack?: () => void;
}

export function Step1Service({ state, dispatch, onContinue, onBack }: Step1ServiceProps): JSX.Element {
  const [category, setCategory] = useState<ServiceCategory>('personal');

  const filtered = RAE_SERVICES.filter((s) => s.category === category);

  return (
    <main className="rae-wizard__main">
      {onBack && (
        <button type="button" className="rae-wizard__back" onClick={onBack}>
          ← Back
        </button>
      )}
      <h1 className="rae-wizard__h1">What can we help you with?</h1>
      <p className="rae-wizard__lead">
        Pick the service you'd like help with. We'll match you with an employee-owner who can handle it.
      </p>

      <div className="rae-svc-tabs" role="tablist" aria-label="Service category">
        {(['personal', 'business', 'trust'] as const).map((cat) => (
          <button
            key={cat}
            type="button"
            role="tab"
            aria-selected={category === cat}
            className={`rae-svc-tab ${category === cat ? 'is-active' : ''}`.trim()}
            onClick={() => setCategory(cat)}
          >
            {cat === 'personal' ? 'Personal Banking' : cat === 'business' ? 'Business' : 'Trust / Estate'}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p style={{ color: 'var(--ink-600)', fontSize: 13, padding: 'var(--sp-9) 0' }}>
          No services in this category yet. Please select Personal Banking.
        </p>
      ) : (
        <div className="rae-svc-list" role="radiogroup" aria-label="Service type">
          {filtered.map((svc) => {
            const isSelected = state.service?.code === svc.code;
            return (
              <button
                key={svc.code}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`rae-svc ${isSelected ? 'is-selected' : ''}`.trim()}
                onClick={() => dispatch({ type: 'setService', service: svc as RaeService })}
              >
                <span className="rae-svc__icon" aria-hidden>{svc.icon}</span>
                <span className="rae-svc__meta">
                  <span className="rae-svc__title">{svc.title}</span>
                  <span className="rae-svc__dur">● {svc.durationMin} min</span>
                  <span className="rae-svc__desc">{svc.description}</span>
                </span>
                <span className="rae-svc__arrow" aria-hidden>›</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="rae-wizard__actions">
        <span />
        <Button
          variant="primary"
          size="lg"
          disabled={!state.service}
          onClick={onContinue}
          aria-label="Continue to meeting method"
        >
          Continue →
        </Button>
      </div>
    </main>
  );
}
